<template>
  <!-- View mode: plain, read-only rows -->
  <div v-if="mode === 'view'" class="sfs-section sfs-section--view">
    <div v-if="schemas.length === 0" class="sfs-empty">No schemas.</div>
    <div
      v-for="schema in schemas"
      :key="schema.schema_id"
      class="sfs-schema sfs-schema--view"
    >
      <div class="sfs-schema-header">
        <span class="sfs-schema-name">
          <ion-icon :icon="libraryOutline" class="sfs-schema-icon" />
          {{ schema.name }}
        </span>
      </div>

      <div v-for="entry in schema.entries" :key="entry.id" class="sfs-entry">
        <template v-if="entry.entry_type === 'schema_ref'">
          <div class="sfs-entry-subschema">
            <ion-icon :icon="libraryOutline" class="sfs-subschema-icon" />
            <span class="sfs-entry-name">{{
              entry.sub_schema_name ?? `Schema #${entry.sub_schema_id}`
            }}</span>
            <div
              v-if="entry.sub_entries && entry.sub_entries.length"
              class="sfs-sub-entries"
            >
              <div
                v-for="sub in entry.sub_entries"
                :key="sub.id"
                class="sfs-entry sfs-entry--nested"
              >
                <span class="sfs-entry-name">{{ sub.name }}</span>
                <div class="sfs-entry-value">
                  <SchemaEntryValueArea
                    :entry="sub"
                    :objectId="objectId"
                    mode="view"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="sfs-entry-header">
            <span class="sfs-entry-name">{{ entry.name }}</span>
          </div>
          <div class="sfs-entry-value">
            <SchemaEntryValueArea
              :entry="entry"
              :objectId="objectId"
              mode="view"
            />
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Edit mode -->
  <div v-else class="sfs-section">
    <div v-for="schema in schemas" :key="schema.schema_id" class="sfs-schema">
      <div class="sfs-schema-header">
        <span class="sfs-schema-name">
          <ion-icon :icon="libraryOutline" class="sfs-schema-icon" />
          {{ schema.name }}
        </span>
        <div class="sfs-schema-actions">
          <ion-button
            fill="clear"
            size="small"
            color="danger"
            @click="confirmRemoveSchema(schema)"
          >
            <ion-icon slot="icon-only" :icon="closeCircleOutline" />
          </ion-button>
        </div>
      </div>

      <div v-if="schema.entries.length === 0" class="sfs-empty">
        No fields in this schema.
      </div>
      <div v-for="entry in schema.entries" :key="entry.id" class="sfs-entry">
        <template v-if="entry.entry_type === 'schema_ref'">
          <div class="sfs-entry-subschema">
            <ion-icon :icon="libraryOutline" class="sfs-subschema-icon" />
            <span class="sfs-entry-name">{{
              entry.sub_schema_name ?? `Schema #${entry.sub_schema_id}`
            }}</span>
            <div
              v-if="entry.sub_entries && entry.sub_entries.length"
              class="sfs-sub-entries"
            >
              <div
                v-for="sub in entry.sub_entries"
                :key="sub.id"
                class="sfs-entry sfs-entry--nested"
              >
                <span class="sfs-entry-name">{{ sub.name }}</span>
                <div class="sfs-entry-value">
                  <SchemaEntryValueArea
                    :ref="setEntryRef(sub.id)"
                    :entry="sub"
                    :objectId="objectId"
                    mode="edit"
                    @refresh="$emit('refresh')"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="sfs-entry-header">
            <span class="sfs-entry-name">{{ entry.name }}</span>
          </div>
          <div class="sfs-entry-value">
            <SchemaEntryValueArea
              :ref="setEntryRef(entry.id)"
              :entry="entry"
              :objectId="objectId"
              mode="edit"
              @refresh="$emit('refresh')"
            />
          </div>
        </template>
      </div>
    </div>

    <ion-button
      expand="block"
      fill="outline"
      size="small"
      @click="chooseSchemaOpen = true"
    >
      <ion-icon slot="start" :icon="addOutline" />
      Add schema
    </ion-button>

    <ion-modal
      :is-open="chooseSchemaOpen"
      @did-dismiss="chooseSchemaOpen = false"
    >
      <ChooseSchemaModal
        :objectId="objectId"
        :appliedSchemaIds="appliedSchemaIds"
        @save="onSchemaChosen"
        @close="chooseSchemaOpen = false"
      />
    </ion-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowReactive } from "vue";
import { IonButton, IonIcon, IonModal, alertController } from "@ionic/vue";
import { addOutline, closeCircleOutline, libraryOutline } from "ionicons/icons";
import type { ObjectSchemaOut } from "@/models/custom-schemas";
import { useApiStore } from "@/stores/api";
import ChooseSchemaModal from "@/components/modals/ChooseSchemaModal.vue";
import SchemaEntryValueArea from "@/components/SchemaEntryValueArea.vue";

const props = withDefaults(
  defineProps<{
    objectId: number;
    schemas: ObjectSchemaOut[];
    mode?: "view" | "edit";
  }>(),
  {
    mode: "edit",
  }
);

const emit = defineEmits<{
  refresh: [];
}>();

const api = useApiStore();
const chooseSchemaOpen = ref(false);

const appliedSchemaIds = computed(() => props.schemas.map((s) => s.schema_id));

// reactive, so a draft snapshot that reads the entries notices entries that mount later
const entryRefs = shallowReactive(new Map<number, any>());
function setEntryRef(entryId: number) {
  return (el: any) => {
    if (el) entryRefs.set(entryId, el);
    else entryRefs.delete(entryId);
  };
}

async function onSchemaChosen(
  schemaId: import("@/models/branded-ids").CustomSchemaId
) {
  chooseSchemaOpen.value = false;
  await api.applySchemaToObject({ objectId: props.objectId, schemaId });
  emit("refresh");
}

async function confirmRemoveSchema(schema: ObjectSchemaOut) {
  const alert = await alertController.create({
    header: "Remove schema",
    message: `Remove "${schema.name}" from this item? Values for its fields will be deleted.`,
    buttons: [
      { text: "Cancel", role: "cancel" },
      {
        text: "Remove",
        role: "destructive",
        handler: async () => {
          await api.removeSchemaFromObject({
            objectId: props.objectId,
            schemaId: schema.schema_id,
          });
          emit("refresh");
        },
      },
    ],
  });
  await alert.present();
}

async function flush() {
  await Promise.all(Array.from(entryRefs.values()).map((r) => r.flush()));
}

function reset() {
  for (const r of entryRefs.values()) r.reset();
}

function getDraft(): Record<string, any> {
  return Object.assign({}, ...Array.from(entryRefs.values()).map((r) => r.getDraft()));
}

function applyDraft(values: Record<string, any>) {
  for (const r of entryRefs.values()) r.applyDraft(values);
}

defineExpose({ flush, reset, getDraft, applyDraft });
</script>

<style scoped>
.sfs-section {
  width: 100%;
  padding: 4px 0;
}

.sfs-schema {
  border: 1px solid var(--ion-color-medium-tint, #ccc);
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}

.sfs-schema-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--ion-color-light, #f4f5f8);
}

.sfs-schema-name {
  font-weight: 600;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sfs-schema-icon {
  font-size: 1em;
  color: var(--ion-color-primary);
}

.sfs-schema-actions {
  display: flex;
  gap: 2px;
}

.sfs-empty {
  padding: 8px 12px;
  color: var(--ion-color-medium);
  font-size: 0.85em;
}

.sfs-entry {
  padding: 6px 12px;
  border-top: 1px solid var(--ion-color-light-shade, #eee);
}

.sfs-entry--nested {
  padding-left: 24px;
}

.sfs-entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sfs-entry-name {
  font-size: 0.85em;
  color: var(--ion-color-medium-shade);
  font-weight: 500;
}

.sfs-entry-value {
  margin-top: 2px;
}

.sfs-entry-subschema {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sfs-subschema-icon {
  font-size: 0.9em;
  color: var(--ion-color-medium);
}

.sfs-sub-entries {
  padding-left: 8px;
  border-left: 2px solid var(--ion-color-light-shade);
}

/* View mode */
.sfs-section--view {
  padding: 4px 0;
}

.sfs-schema--view {
  border: none;
  border-radius: 0;
  margin-bottom: 12px;
}

.sfs-schema--view .sfs-schema-header {
  background: transparent;
  padding: 2px 0;
}
</style>
